import { useState, useCallback, useMemo } from 'react';

import { groupBy } from '../utils/arrayUtils';
import {
  calculateSalesTaxAmount,
  calculateLineItemSubtotal,
  calculateSubtotalWithTax,
  sumTaxRates,
} from '../utils/taxCalculations';

import { SalesTaxViewModel } from '@models/SalesTax';

/**
 * Base interface for line item sales tax
 */
export interface BaseLineItemSalesTax {
  id: number;
  salesTaxID: number;
  salesTaxAbbreviation: string;
  salesTaxAmount: number;
  rate: number;
}

/**
 * Base interface for line items
 */
export interface BaseLineItem {
  id: number;
  productID: number;
  description: string;
  quantity: number;
  price: number;
  subTotal: number;
}

/**
 * Configuration for the line items hook
 */
export interface UseLineItemsConfig<
  TLineItem extends BaseLineItem,
  TSalesTax extends BaseLineItemSalesTax
> {
  /** Default value for a new line item */
  defaultLineItem: TLineItem;
  /** Default value for a new sales tax entry */
  defaultSalesTax: TSalesTax;
  /** Key to access the sales taxes array on the line item */
  salesTaxesKey: keyof TLineItem;
  /** Optional callback for validation messages */
  onValidationError?: (message: string) => void;
}

export interface UseLineItemsOptions {
  onInfo?: (message: string) => void;
}

/**
 * Generic hook for managing line items (estimates, invoices, recurring invoices)
 */
export const useLineItems = <
  TLineItem extends BaseLineItem,
  TSalesTax extends BaseLineItemSalesTax
>(
  config: UseLineItemsConfig<TLineItem, TSalesTax>,
  options: UseLineItemsOptions = {}
) => {
  const { defaultLineItem, defaultSalesTax, salesTaxesKey, onValidationError } =
    config;
  const { onInfo } = options;

  // Create initial line item with default sales tax
  const createEmptyLineItem = useCallback((): TLineItem => {
    return {
      ...defaultLineItem,
      [salesTaxesKey]: [defaultSalesTax],
    } as TLineItem;
  }, [defaultLineItem, defaultSalesTax, salesTaxesKey]);

  const [lineItems, setLineItems] = useState<TLineItem[]>([
    createEmptyLineItem(),
  ]);

  /**
   * Validates that all line items have a product selected
   */
  const validateLineItems = useCallback((): boolean => {
    for (const item of lineItems) {
      if (!item.productID) {
        return false;
      }
    }
    return true;
  }, [lineItems]);

  /**
   * Adds a new empty line item
   */
  const addLineItem = useCallback(() => {
    if (!validateLineItems()) {
      const message = 'Please fill in the fields before adding a new product.';
      onInfo?.(message);
      onValidationError?.(message);
      return false;
    }

    setLineItems((prev) => [...prev, createEmptyLineItem()]);
    return true;
  }, [validateLineItems, createEmptyLineItem, onInfo, onValidationError]);

  /**
   * Removes a line item at the specified index
   */
  const removeLineItem = useCallback(
    (index: number) => {
      if (lineItems.length <= 1) {
        console.log('At least one item must remain in the table.');
        return false;
      }

      setLineItems((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
      return true;
    },
    [lineItems.length]
  );

  /**
   * Resets a line item at the specified index to empty state
   */
  const resetLineItem = useCallback(
    (index: number) => {
      setLineItems((prev) => {
        const updated = [...prev];
        updated[index] = createEmptyLineItem();
        return updated;
      });
    },
    [createEmptyLineItem]
  );

  /**
   * Updates the quantity for a line item and recalculates totals
   */
  const updateLineItemQuantity = useCallback(
    (index: number, quantity: number, isTaxExempt: boolean) => {
      setLineItems((prev) => {
        const updated = [...prev];
        const item = { ...updated[index] };

        item.quantity = quantity;

        // Get sales taxes array
        const salesTaxes = (item[salesTaxesKey] as TSalesTax[]) || [];

        // Calculate total tax rate
        const totalTaxRate = isTaxExempt
          ? 0
          : sumTaxRates(salesTaxes, (tax) => tax.salesTaxID !== 0);

        // Calculate subtotal with tax
        item.subTotal = calculateSubtotalWithTax(
          item.price,
          quantity,
          totalTaxRate,
          isTaxExempt
        );

        // Update sales tax amounts
        const updatedSalesTaxes = salesTaxes.map((tax) => ({
          ...tax,
          salesTaxAmount: calculateSalesTaxAmount(
            item.price,
            quantity,
            tax.rate,
            isTaxExempt
          ),
        }));

        item[salesTaxesKey] =
          updatedSalesTaxes as TLineItem[typeof salesTaxesKey];
        updated[index] = item;

        return updated;
      });
    },
    [salesTaxesKey]
  );

  /**
   * Updates a line item with product data
   */
  const updateLineItemFromProduct = useCallback(
    (
      index: number,
      productData: {
        productID: number;
        description: string;
        price: number;
        quantity: number;
        productSalesTaxes: TSalesTax[];
      },
      isTaxExempt: boolean
    ) => {
      setLineItems((prevLineItems) => {
        const newLineItems = [...prevLineItems];
        const currentItem = newLineItems[index];

        // Map product sales taxes to line item sales taxes
        const newSalesTaxes = productData.productSalesTaxes.map(
          (productTax) => ({
            ...defaultSalesTax,
            salesTaxID: (productTax as any).salesTaxID,
            rate: (productTax as any).rate,
            salesTaxAbbreviation: (productTax as any).salesTaxAbbreviation,
          })
        );

        const updatedItem: TLineItem = {
          ...currentItem,
          productID: productData.productID,
          description: productData.description,
          price: productData.price,
          quantity: productData.quantity,
          [salesTaxesKey]: newSalesTaxes,
        };

        // Recalculate subtotal and tax amounts
        const subTotal = calculateLineItemSubtotal(
          updatedItem.price,
          updatedItem.quantity
        );
        updatedItem.subTotal = subTotal;

        const lineItemSalesTaxes = updatedItem[
          salesTaxesKey
        ] as unknown as TSalesTax[];

        const updatedSalesTaxes = lineItemSalesTaxes.map((tax) => ({
          ...tax,
          salesTaxAmount: calculateSalesTaxAmount(
            updatedItem.price,
            updatedItem.quantity,
            tax.rate,
            isTaxExempt
          ),
        }));

        (updatedItem[salesTaxesKey] as unknown) = updatedSalesTaxes;

        newLineItems[index] = updatedItem;
        return newLineItems;
      });
    },
    [salesTaxesKey, defaultSalesTax]
  );

  /**
   * Clears a line item (sets to empty/default)
   */
  const clearLineItem = useCallback(
    (index: number) => {
      setLineItems((prev) => {
        const updated = [...prev];
        updated[index] = createEmptyLineItem();
        return updated;
      });
    },
    [createEmptyLineItem]
  );

  /**
   * Updates a sales tax on a line item
   */
  const updateLineItemSalesTax = useCallback(
    (
      lineItemIndex: number,
      salesTaxIndex: number,
      salesTax: SalesTaxViewModel | undefined,
      isTaxExempt: boolean
    ) => {
      setLineItems((prev) => {
        const updated = [...prev];
        const item = { ...updated[lineItemIndex] };
        const salesTaxes = [...((item[salesTaxesKey] as TSalesTax[]) || [])];

        if (!salesTax || salesTax.id === 0) {
          // Remove the sales tax if it's not the last one
          if (salesTaxIndex < salesTaxes.length - 1) {
            salesTaxes.splice(salesTaxIndex, 1);
          }
        } else {
          // Check if this tax already exists
          const existingIndex = salesTaxes.findIndex(
            (t) => t.salesTaxID === salesTax.id
          );
          if (existingIndex !== -1 && existingIndex !== salesTaxIndex) {
            // Tax already exists, don't add duplicate
            return prev;
          }

          // Update the sales tax
          salesTaxes[salesTaxIndex] = {
            ...salesTaxes[salesTaxIndex],
            salesTaxID: salesTax.id,
            salesTaxAbbreviation: salesTax.abbreviation,
            rate: salesTax.rate,
            salesTaxAmount: calculateSalesTaxAmount(
              item.price,
              item.quantity,
              salesTax.rate,
              isTaxExempt
            ),
          };

          // Add empty tax option if the last one is now filled
          if (salesTaxes[salesTaxes.length - 1].salesTaxID !== 0) {
            salesTaxes.push({ ...defaultSalesTax } as TSalesTax);
          }
        }

        item[salesTaxesKey] = salesTaxes as TLineItem[typeof salesTaxesKey];
        updated[lineItemIndex] = item;

        return updated;
      });
    },
    [salesTaxesKey, defaultSalesTax]
  );

  /**
   * Updates all line items with new effective tax rates
   */
  const updateSalesTaxRates = useCallback(
    (salesTaxEffectiveRates: SalesTaxViewModel[], isTaxExempt: boolean) => {
      setLineItems((prev) => {
        return prev.map((item) => {
          const salesTaxes = (item[salesTaxesKey] as TSalesTax[]) || [];

          const updatedSalesTaxes = salesTaxes.map((tax) => {
            const effectiveRate = salesTaxEffectiveRates.find(
              (r) => r.id === tax.salesTaxID
            );
            const rate = effectiveRate?.rate ?? tax.rate;

            return {
              ...tax,
              rate,
              salesTaxAmount: calculateSalesTaxAmount(
                item.price,
                item.quantity,
                rate,
                isTaxExempt
              ),
            };
          });

          return {
            ...item,
            [salesTaxesKey]: updatedSalesTaxes,
          };
        });
      });
    },
    [salesTaxesKey]
  );

  /**
   * Recalculates all tax amounts based on tax exempt status
   */
  const recalculateTaxAmounts = useCallback(
    (isTaxExempt: boolean) => {
      setLineItems((prev) => {
        return prev.map((item) => {
          const salesTaxes = (item[salesTaxesKey] as TSalesTax[]) || [];

          const updatedSalesTaxes = salesTaxes.map((tax) => ({
            ...tax,
            salesTaxAmount: calculateSalesTaxAmount(
              item.price,
              item.quantity,
              tax.rate,
              isTaxExempt
            ),
          }));

          return {
            ...item,
            [salesTaxesKey]: updatedSalesTaxes,
          };
        });
      });
    },
    [salesTaxesKey]
  );

  /**
   * Gets grouped sales taxes by abbreviation for summary display
   */
  const getSalesTaxesBySalesTaxID = useMemo(() => {
    const allSalesTaxes = lineItems.flatMap((item) =>
      ((item[salesTaxesKey] as TSalesTax[]) || [])
        .filter((tax) => tax.salesTaxID !== 0)
        .map((tax) => ({ ...tax, salesTaxRate: tax.rate }))
    );

    return groupBy(allSalesTaxes, 'salesTaxAbbreviation');
  }, [lineItems, salesTaxesKey]);

  return {
    // State
    lineItems,

    // Actions
    addLineItem,
    removeLineItem,
    resetLineItem,
    updateLineItemQuantity,
    updateLineItemFromProduct,
    clearLineItem,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    validateLineItems,

    // Computed
    getSalesTaxesBySalesTaxID,

    // Setters
    setLineItems,
    createEmptyLineItem,
  };
};
